import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Pencil } from "lucide-react";
import { usePage } from "@/lib/usePage";
import { SectionRenderer } from "@/components/sections/SectionRenderer";
import { Loader } from "@/components/common/Loader";
import { useAuth } from "@/context/AuthContext";
import { VisualEditor } from "@/components/editor/VisualEditor";

// Public page = ordered published sections rendered in order.
// Authenticated staff (admin/editor) get an inline "Edit Page" entry that turns the
// live page into an in-context editor (also openable via ?edit=1). Visitors never do.
export const PageRenderer = ({ pageKey }) => {
  const { sections, loading } = usePage(pageKey);
  const { user } = useAuth();
  const isStaff = user && (user.role === "admin" || user.role === "editor");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [editing, setEditing] = useState(false);

  useEffect(() => { setEditing(params.get("edit") === "1"); }, [params]);

  if (isStaff && editing) {
    return (
      <VisualEditor
        pageKey={pageKey}
        onExit={() => { setEditing(false); navigate(location.pathname, { replace: true }); }}
      />
    );
  }

  if (loading) return <div className="min-h-[60vh]"><Loader /></div>;

  return (
    <div data-testid={`page-${pageKey}`}>
      {sections.map((s) => <SectionRenderer key={s.id} section={s} />)}
      {isStaff && (
        <button
          onClick={() => setEditing(true)}
          data-testid="inline-edit-page-button"
          className="fixed bottom-24 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-lg shadow-black/40 transition-colors hover:bg-[hsl(var(--brand-red-deep))] lg:bottom-6"
        >
          <Pencil className="h-4 w-4" /> Edit Page
        </button>
      )}
    </div>
  );
};

export default PageRenderer;
